import { COMPANY, amountToWordsInr, formatCompanyLocality, type CreditNoteDto } from '@gty/common';

import { cn } from '../ui/cn';
import { formatCurrency, formatShortDate } from '../ui/format';

/**
 * The A4 credit note, built from `documentation/design/images/Credit-Note.jpeg`.
 *
 * FIXED 794px WIDE — the same convention the invoice and voucher use. At 96dpi
 * that is exactly A4 (210mm), so what renders on screen is what prints, and the
 * `@media print` rules in index.css key off `data-print-sheet`. Nothing here is
 * responsive on purpose: a document that reflows is a document that prints
 * differently from its preview.
 *
 * The layout follows the artwork band for band: brand header with the meta
 * table, trust strip, Customer/Tour cards, credit table, three lower cards,
 * signature band, footer.
 */

/** Red section tab — the artwork's rounded label sitting on a card's top edge. */
function SectionTab({ icon, label }: { icon: JSX.Element; label: string }): JSX.Element {
  return (
    <div className="absolute -top-[13px] left-3 z-10 inline-flex items-center gap-[7px] rounded-[7px] bg-brand px-[11px] py-[6px] text-white">
      {icon}
      <span className="text-[11px] font-bold uppercase leading-none tracking-[.6px]">{label}</span>
    </div>
  );
}

/** A labelled row inside the Customer / Tour cards: "Label  :  value". */
function Field({
  label,
  value,
  wide = false,
}: {
  label: string;
  value: string;
  wide?: boolean;
}): JSX.Element {
  return (
    <div className="flex items-baseline gap-2">
      <span className={cn('flex-none text-[9.5px] leading-[1.5]', wide ? 'w-[112px]' : 'w-[92px]')}>
        {label}
      </span>
      <span className="flex-none text-[9.5px] leading-[1.5]">:</span>
      <span className="min-w-0 flex-1 border-b border-dotted border-neutral-400 pb-[2px] text-[9.5px] font-semibold leading-[1.5]">
        {value}
      </span>
    </div>
  );
}

function MetaRow({ label, value, accent }: { label: string; value: string; accent?: boolean }): JSX.Element {
  return (
    <div className="flex items-stretch border-b border-neutral-200 last:border-b-0">
      <div className="flex w-[26px] flex-none items-center justify-center bg-brand">
        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2">
          <rect x="3" y="4" width="18" height="17" rx="2" />
          <path d="M8 2v4M16 2v4M3 10h18" />
        </svg>
      </div>
      <div className="flex-1 px-[9px] py-[6px] text-[9.5px] font-semibold leading-none">{label}</div>
      <div
        className={cn(
          'w-[128px] flex-none px-[9px] py-[6px] text-[9.5px] font-bold leading-none',
          accent === true ? 'text-brand' : 'text-ink',
        )}
      >
        {value}
      </div>
    </div>
  );
}

const TRUST_ITEMS: ReadonlyArray<[string, string]> = [
  ['SAFE JOURNEY', 'OUR PRIORITY'],
  ['EXPERIENCED', 'TEAM'],
  ['BEST SERVICE', 'GUARANTEE'],
  ['24x7 CUSTOMER', 'SUPPORT'],
];

export function CreditNoteDocument({ note }: { note: CreditNoteDto }): JSX.Element {
  const guests = `Adults: ${note.adults}    Kids: ${note.kids}`;

  return (
    <article
      data-print-sheet
      className="relative flex h-[1123px] w-[794px] flex-none flex-col overflow-hidden bg-white text-ink shadow-[0_10px_34px_rgba(0,0,0,.14)]"
    >

      {/* ── header ─────────────────────────────────────────────────────── */}
      <header className="relative px-7 pt-5">
        {/* items-center puts the brand block midway between the page edge and the
            first rule — same treatment as the voucher letterhead. */}
        <div className="flex items-center gap-4">
          <img src="/brand/guruji-mark.png" alt="" className="h-auto w-[74px] flex-none" />
          <div className="pt-[2px]">
            <div className="font-heading text-[34px] leading-[.92] tracking-[-1px] text-brand">
              GURUJI
            </div>
            <div className="mt-[6px] text-[19px] font-bold leading-none tracking-[1.2px]">
              TRAVEL YATRA
            </div>
            <div className="mt-[7px] text-[11.5px] leading-none">
              {COMPANY.tagline}{' '}
              <span className="font-semibold text-brand">{COMPANY.taglineAccent}</span>
            </div>
          </div>

          <div className="ml-auto w-[330px] flex-none">
            <div className="text-right font-heading text-[30px] leading-none tracking-[-.6px] text-brand">
              CREDIT NOTE
            </div>
            <div className="mt-[7px] text-right text-[8.6px] leading-[1.45] text-neutral-700">
              Issued Against Cancellation / Adjustment /<br />
              Overpayment / Other Reasons
            </div>
            <div className="mt-[10px] overflow-hidden border border-neutral-300">
              <MetaRow label="Credit Note No." value={note.ref} accent />
              <MetaRow label="Credit Note Date" value={formatShortDate(note.issuedAt)} />
              <MetaRow label="Original Invoice No." value={note.invoiceRef} />
              <MetaRow label="Invoice Date" value={formatShortDate(note.invoiceDate)} />
              <MetaRow label="Booking No." value={note.bookingRef} />
              <MetaRow label="Booking Date" value={formatShortDate(note.bookingDate)} />
            </div>
          </div>
        </div>
      </header>

      {/* ── trust strip + refund callout ───────────────────────────────── */}
      <div className="mt-3 flex items-stretch gap-4 px-7">
        <div className="flex flex-1 items-center gap-[14px]">
          {TRUST_ITEMS.map(([top, bottom], index) => (
            <div key={top} className="flex items-center gap-[14px]">
              {index > 0 && <span className="h-[26px] w-px bg-neutral-300" />}
              <div className="flex items-center gap-[7px]">
                <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="#E10600" strokeWidth="1.8">
                  <circle cx="12" cy="12" r="9" />
                  <path d="m8.5 12 2.4 2.4 4.6-4.8" />
                </svg>
                <div className="text-[7.6px] font-semibold uppercase leading-[1.35] tracking-[.3px] text-neutral-700">
                  {top}
                  <br />
                  {bottom}
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="flex w-[326px] flex-none items-center gap-[11px] rounded-[8px] border border-brand/30 px-[13px] py-[7px]">
          <span className="grid h-[30px] w-[30px] flex-none place-items-center rounded-full bg-brand/10 font-heading text-[15px] text-brand">
            ₹
          </span>
          <div>
            <div className="text-[10.5px] font-bold uppercase leading-none tracking-[.4px] text-brand">
              Refund / Adjustment
            </div>
            <div className="mt-[5px] text-[8.4px] leading-[1.45] text-neutral-700">
              This Credit Note is valid for future use and not refundable in cash.
            </div>
          </div>
        </div>
      </div>

      {/* ── customer + tour ─────────────────────────────────────────────── */}
      <div className="mt-3 grid grid-cols-2 gap-3 px-7">
        <div className="relative rounded-[10px] border border-brand/30 px-4 pb-3 pt-[18px]">
          <SectionTab
            label="Customer Details"
            icon={
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.1">
                <circle cx="12" cy="8" r="4" />
                <path d="M4 21c0-4 3.6-6.5 8-6.5s8 2.5 8 6.5" />
              </svg>
            }
          />
          <div className="flex flex-col gap-[9px]">
            <Field label="Lead Guest Name" value={note.customerName} />
            <Field label="Contact Number" value={note.customerPhone} />
            <Field label="Email ID" value={note.customerEmail} />
            <Field label="Address" value={note.customerAddress} />
            <div className="h-[6px]" />
            <Field label="Total Guests" value={guests} />
            <Field label="ID Proof" value={note.idProof} />
          </div>
        </div>

        <div className="relative rounded-[10px] border border-brand/30 px-4 pb-3 pt-[18px]">
          <SectionTab
            label="Tour Details"
            icon={
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.1">
                <rect x="3" y="7" width="18" height="13" rx="2" />
                <path d="M9 7V5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
              </svg>
            }
          />
          <div className="flex flex-col gap-[9px]">
            <Field label="Package Name" value={note.packageName} wide />
            <Field label="Travel Date" value={formatShortDate(note.travelDate)} wide />
            <Field label="Return Date" value={formatShortDate(note.returnDate)} wide />
            <Field label="Destination" value={note.destination} wide />
            <Field label="Departure City" value={note.departureCity} wide />
            <Field label="Reason for Credit Note" value={note.reason} wide />
          </div>
        </div>
      </div>

      {/* ── credit details ──────────────────────────────────────────────── */}
      <div className="relative mt-3 px-7">
        <SectionTab
          label="Credit Details"
          icon={
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.1">
              <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
              <path d="M14 3v5h5M9 13h6M9 17h4" />
            </svg>
          }
        />
        <table className="w-full border-collapse overflow-hidden rounded-[8px] border border-neutral-300">
          <thead>
            <tr className="bg-brand text-white">
              <th className="w-[54px] px-2 py-[7px] text-center text-[8.6px] font-bold uppercase tracking-[.5px]">
                Sr. No.
              </th>
              <th className="px-3 py-[6px] text-center text-[8.6px] font-bold uppercase tracking-[.5px]">
                Description
              </th>
              <th className="w-[196px] px-3 py-[6px] text-center text-[8.6px] font-bold uppercase tracking-[.5px]">
                Reference / Service
              </th>
              <th className="w-[128px] px-3 py-[6px] text-center text-[8.6px] font-bold uppercase tracking-[.5px]">
                Amount (₹)
              </th>
            </tr>
          </thead>
          <tbody>
            {note.lines.map((line) => (
              <tr key={line.id} className="border-t border-neutral-200">
                <td className="px-2 py-[7px] text-center text-[9.5px] tabular-nums">{line.srNo}</td>
                <td className="px-3 py-[6px] text-[9.5px] leading-[1.4]">{line.description}</td>
                <td className="px-3 py-[6px] text-[9.5px] leading-[1.4] text-neutral-700">
                  {line.reference}
                </td>
                <td className="px-3 py-[6px] text-right text-[9.5px] font-semibold tabular-nums">
                  {formatCurrency(line.amount)}
                </td>
              </tr>
            ))}
            <tr className="border-t border-neutral-300 bg-brand/[.07]">
              <td colSpan={3} className="px-3 py-[9px] text-right text-[10.5px] font-bold uppercase tracking-[.4px] text-brand">
                Total Credit Amount
              </td>
              <td className="px-3 py-[9px] text-right font-heading text-[13px] tabular-nums text-brand">
                {formatCurrency(note.totalCreditAmount)}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* ── adjustment · terms · office ─────────────────────────────────── */}
      <div className="mt-3 grid grid-cols-[1fr_1fr_1fr] gap-3 px-7">
        <div className="relative rounded-[10px] border border-brand/30 px-4 pb-3 pt-[18px]">
          <SectionTab
            label="Payment Adjustment"
            icon={<span className="text-[10px] font-bold leading-none">₹</span>}
          />
          <div className="flex flex-col gap-[7px]">
            {[
              ['Total Amount (Original Invoice)', note.originalInvoiceAmount],
              ['Total Paid Amount', note.totalPaidAmount],
              ['Total Credit Amount (This Note)', note.totalCreditAmount],
              ['Balance Adjustment / Carry Forward', note.balanceAdjustment],
            ].map(([label, amount]) => (
              <div key={label as string} className="flex items-baseline gap-2">
                <span className="flex-1 text-[8.8px] leading-[1.35]">{label}</span>
                <span className="text-[8.8px]">₹</span>
                <span className="w-[62px] flex-none text-right text-[9.5px] font-semibold tabular-nums">
                  {formatCurrency(amount as number)}
                </span>
              </div>
            ))}
          </div>

          <div className="mt-[13px] rounded-[7px] bg-brand/[.07] px-3 py-[10px]">
            <div className="text-[8.6px] font-bold uppercase leading-none tracking-[.5px] text-brand">
              Amount in Words
            </div>
            {/*
              DERIVED from `totalCreditAmount`, never stored. Words and digits
              that can disagree are worse on a financial document than either
              alone — see `amountToWordsInr`.
            */}
            <div className="mt-[7px] text-[9px] font-semibold leading-[1.45]">
              {amountToWordsInr(note.totalCreditAmount)}
            </div>
          </div>
        </div>

        <div className="relative rounded-[10px] border border-brand/30 px-4 pb-3 pt-[18px]">
          <SectionTab
            label="Terms & Conditions"
            icon={
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.1">
                <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
                <path d="M14 3v5h5" />
              </svg>
            }
          />
          <ul className="flex flex-col gap-[7px]">
            {note.terms.map((term) => (
              <li key={term} className="flex gap-[7px] text-[8.6px] leading-[1.45]">
                <span className="mt-[5px] h-[3px] w-[3px] flex-none rounded-full bg-brand" />
                <span>{term}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="relative rounded-[10px] border border-brand/30 px-4 pb-3 pt-[18px]">
          <SectionTab
            label="For Office Use Only"
            icon={
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.1">
                <circle cx="12" cy="12" r="3" />
                <path d="M19.4 15a1.6 1.6 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.6 1.6 0 0 0-2.7 1.1V21a2 2 0 1 1-4 0v-.1A1.6 1.6 0 0 0 7 19.4a1.6 1.6 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1A1.6 1.6 0 0 0 3 14.1H3a2 2 0 1 1 0-4h.1A1.6 1.6 0 0 0 4.6 7a1.6 1.6 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1A1.6 1.6 0 0 0 9.9 3H10a2 2 0 1 1 4 0v.1a1.6 1.6 0 0 0 2.7 1.1l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.6 1.6 0 0 0 1.1 2.7H21a2 2 0 1 1 0 4h-.1a1.6 1.6 0 0 0-1.5 1.3z" />
              </svg>
            }
          />
          <div className="flex flex-col gap-[9px]">
            <Field label="Prepared By" value={note.preparedBy} />
            <Field label="Checked By" value={note.checkedBy} />
            <Field label="Approved By" value={note.approvedBy} />
            <Field label="Remarks" value={note.remarks} />
          </div>
          <div className="mt-[13px] flex items-center gap-3">
            <QrPlaceholder />
            <div className="text-[8.4px] leading-[1.4] text-neutral-700">
              Scan to Verify
              <br />
              Credit Note
            </div>
          </div>
        </div>
      </div>

      {/* ── signatures ─────────────────────────────────────────────────── */}
      <div className="mt-3 grid grid-cols-2 gap-3 px-7">
        {([
          ['Customer Signature', ''],
          ['Authorized Signatory', COMPANY.displayName],
        ] as ReadonlyArray<readonly [string, string]>).map(([label, sub]) => (
          <div key={label} className="relative rounded-[10px] border border-brand/30 px-4 pb-[14px] pt-[22px]">
            <SectionTab
              label={label}
              icon={
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.1">
                  <circle cx="12" cy="8" r="4" />
                  <path d="M4 21c0-4 3.6-6.5 8-6.5s8 2.5 8 6.5" />
                </svg>
              }
            />
            <div className="mt-[18px] border-b border-neutral-400" />
            <div className="mt-[7px] flex items-baseline gap-2 text-[8.8px]">
              <span>Date :</span>
              <span className="flex-1 border-b border-dotted border-neutral-400" />
            </div>
            {sub !== '' && (
              <div className="mt-[6px] text-[8.4px] font-semibold uppercase tracking-[.4px] text-neutral-600">
                {sub}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* ── footer ─────────────────────────────────────────────────────── */}
      <footer className="mt-auto flex items-stretch bg-gradient-to-r from-brand to-brand-dark px-7 py-[13px] text-white">
        <div className="flex flex-none items-center gap-[10px] pr-5">
          <img src="/brand/guruji-mark-white.png" alt="" className="h-auto w-[36px] flex-none" />
          <div>
            <div className="text-[13.5px] font-bold uppercase leading-none tracking-[.4px]">
              {COMPANY.displayName}
            </div>
            <div className="mt-[5px] text-[9px] leading-none opacity-90">
              {COMPANY.tagline} {COMPANY.taglineAccent}
            </div>
          </div>
        </div>

        <div className="flex flex-1 items-center gap-5 border-l border-white/25 pl-5 text-[9.4px] leading-[1.5]">
          <div className="flex flex-col gap-[4px]">
            <span>{COMPANY.phone}</span>
            <span>{COMPANY.email}</span>
            <span>{COMPANY.website}</span>
          </div>
          <div className="ml-auto text-right">
            {COMPANY.address.lines.map((line) => (
              <div key={line}>{line}</div>
            ))}
            <div>{formatCompanyLocality()}</div>
            <div className="mt-[5px] inline-flex items-stretch overflow-hidden rounded-[3px]">
              <span className="bg-ink px-[6px] py-[3px] text-[8.6px] font-bold tracking-[.6px]">
                GSTIN
              </span>
              <span className="bg-white px-[6px] py-[3px] text-[8.6px] font-bold tracking-[.4px] text-ink">
                {COMPANY.gstin}
              </span>
            </div>
          </div>
        </div>
      </footer>
    </article>
  );
}

/**
 * Decorative stand-in for the verification QR.
 *
 * The artwork shows a real QR, but there is no verification URL to encode yet
 * and no QR dependency in the project. A deterministic block pattern keeps the
 * layout honest without pretending to be scannable.
 */
function QrPlaceholder(): JSX.Element {
  return (
    <svg viewBox="0 0 21 21" className="h-[62px] w-[62px] flex-none" aria-hidden="true">
      <rect width="21" height="21" fill="#fff" />
      {Array.from({ length: 21 * 21 }, (_, i) => {
        const x = i % 21;
        const y = Math.floor(i / 21);
        const finder =
          (x < 7 && y < 7) || (x > 13 && y < 7) || (x < 7 && y > 13)
            ? (x % 6 === 0 || y % 6 === 0 || (x > 1 && x < 5 && y > 1 && y < 5))
            : false;
        const body = !finder && (x * 7 + y * 13 + x * y) % 3 === 0;
        return finder || body ? <rect key={i} x={x} y={y} width="1" height="1" fill="#201e1d" /> : null;
      })}
    </svg>
  );
}
